import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, Bell, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { value: 70, label: "Bajo", desc: "Más volumen · score ≥ 70" },
  { value: 80, label: "Medio", desc: "Equilibrado · score ≥ 80" },
  { value: 85, label: "Alto (defecto)", desc: "Recomendado · score ≥ 85" },
  { value: 90, label: "Muy alto", desc: "Solo top · score ≥ 90" },
];

export default function PremiumAlertsSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [threshold, setThreshold] = useState(85);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/user-prefs/tender360-premium`);
        if (!alive) return;
        setEnabled(!!r.data?.enabled);
        setThreshold(Number(r.data?.threshold ?? 85));
      } catch {
        // mantiene defaults
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user-prefs/tender360-premium`, { enabled, threshold });
      toast.success("Preferencias guardadas");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando preferencias");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="premium-alerts-settings"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Bell size={16} className="text-amber-600"/> Alertas Premium · Tender360
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Configura tu umbral personal de score Pando. Recibirás un aviso en la campana por cada
              licitación detectada que iguale o supere tu umbral, además del email diario.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <Loader2 className="inline animate-spin mr-2" size={14}/> Cargando…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Toggle enabled */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#007934]"
                data-testid="premium-alerts-enabled"
              />
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  Activar notificaciones en la campana
                </div>
                <div className="text-[11px] text-gray-500">
                  Si desactivas, sólo recibirás el email diario.
                </div>
              </div>
            </label>

            {/* Threshold preset */}
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-2">Umbral de score Pando</div>
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map(p => {
                  const active = threshold === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      disabled={!enabled}
                      onClick={() => setThreshold(p.value)}
                      data-testid={`premium-threshold-${p.value}`}
                      className={`text-left rounded-lg border p-3 transition-all ${
                        active
                          ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      } ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {active && <CheckCircle2 size={12} className="text-amber-600"/>}
                        <span className="text-sm font-bold text-gray-800">{p.label}</span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{p.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className="text-[11px] text-gray-500 w-16">Personal:</span>
                <input
                  type="range"
                  min={50}
                  max={100}
                  step={1}
                  value={threshold}
                  disabled={!enabled}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  className="flex-1 accent-amber-500"
                  data-testid="premium-threshold-slider"
                />
                <span className="text-sm font-mono font-bold w-10 text-right text-amber-700">
                  {threshold}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-[11px] text-gray-600">
              💡 Las notificaciones se marcan como leídas automáticamente después de 24h,
              o al hacer clic en ellas para abrir la licitación.
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="premium-alerts-save"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#007934] text-white hover:bg-[#006528] disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : null}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
